import context from '@truckdown/components-systems';
import { IPromotionManager, PromotionModel, AddFundsModel } from '@truckdown/promotions';
import { defineComponent, ref, Ref, onMounted, onUnmounted, computed } from "vue";
import { showMessage, isCsrOrAdmin } from '../../Utilities/utilities';
import validationError from "../../Validation/Error/Error.vue";
import addCreditCard from "../AddCreditCard/AddCreditCard.vue";
import { CreditCardModel, CreditCardStatus, CreditCardQueryOptions, ICreditCardService, IBillingService, PaymentMethod } from "@truckdown/billing";

interface IData {
    isLoading: Ref<boolean>,
    errors: Ref<{ [key: string]: string }>,

    amount: Ref<number>,
    selectedCreditCard: Ref<CreditCardModel | PaymentMethod | undefined>,
    securityCode: Ref<string>,
    accountCredit: Ref<number>,
    applyCreditToPromotion: Ref<boolean>,
    taxName: Ref<string>,
    taxRate: Ref<number>,

    displayAddCreditCard: Ref<boolean>,
    creditCards: Ref<CreditCardModel[]>
};

const getInitialData = function (props: any) {
    return {
        isLoading: ref<boolean>(false),
        errors: ref<{ [key: string]: string }>({}),

        amount: ref<number>(0),
        selectedCreditCard: ref<CreditCardModel | PaymentMethod | undefined>(),
        securityCode: ref<string>(''),
        accountCredit: ref<number>(0),
        applyCreditToPromotion: ref<boolean>(false),
        taxName: ref<string>(props.taxname),
        taxRate: ref<number>(props.taxrate),

        displayAddCreditCard: ref<boolean>(false),
        creditCards: ref<CreditCardModel[]>([])
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        displayError: computed(() => {
            let errors = data.errors.value;
            let keys = Object.keys(errors);

            let response = {} as { [key: string]: boolean };
            for (let i = 0; i < keys.length; i++) {
                if (errors[keys[i]]) {
                    response[keys[i]] = true;
                }
            }
            return response;
        }),
        totalCharge: computed(() => {
            var value = data.amount.value;

            var taxes = 0;
            if (!data.applyCreditToPromotion.value && data.amount.value > 0) {
                taxes = data.amount.value * (data.taxRate.value / 100);
            }

            if (value && value > 0) {
                return "$" + (value + taxes).toFixed(2);
            } else {
                return "$0.00";
            }
        }),
        taxes: computed(() => {
            if (!data.applyCreditToPromotion.value && data.amount.value > 0) {
                return (data.amount.value * (data.taxRate.value / 100)).toFixed(2);
            }
            return 0.00;
        }),
        showTaxes: computed(() => {
            if (!data.applyCreditToPromotion.value && data.taxRate.value > 0) {
                return true;
            }
            return false;
        })
    }
}

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        validate: function () {
            validateAddFunds(data);
        },
        applyCreditToPromotionChange: function () {
            if (data.applyCreditToPromotion.value === true) {
                data.amount.value = data.accountCredit.value;
            } else {
                data.amount.value = 0;
            }
            validateAddFunds(data);
        },
        addFundsCancelClick: function (): void {
            ctx.emit(events.cancel, false);
        },
        addFundsSaveClick: async function () {
            validateAddFunds(data);
            if (!hasErrors(data)) {
                if (!data.isLoading.value) {
                    data.isLoading.value = true;
                    try {
                        let promotionManager = await context.getService<IPromotionManager>('IPromotionManager');
                        if (data.applyCreditToPromotion.value) {
                            let creditResponse = await promotionManager.addFundsAccountCredit(props.promotion.id, data.accountCredit.value);
                            if (!creditResponse || !creditResponse.ok) {
                                showMessage("Error applying account credit to promotion.", "danger", true, true);
                                ctx.emit(events.cancel);
                            } else {
                                showMessage("Account credit successfully applied to promotion.", "success", true, true);
                                ctx.emit(events.update, creditResponse.data);
                            }
                        } else {
                            let model: AddFundsModel = {
                                promotionId: props.promotion.id,
                                amount: data.amount.value
                            };
                            let card = data.selectedCreditCard.value as any;
                            if (card.id) {
                                model.creditCardId = card.id;
                                model.securityCode = data.securityCode.value;
                            }
                            let response = await promotionManager.addFunds(model);

                            if (!response || !response.ok) {
                                console.log(response);
                                if ((response.message?.search(/declined/i) ?? -1) >= 0) {
                                    showMessage("Payment declined.", "danger", true, true);
                                } else {
                                    showMessage("Error processing payment - please try again or contact your bank.", "danger", true, true);
                                }
                                ctx.emit(events.cancel);
                            } else {
                                showMessage("Funds successfully added to promotion.", "success", true, true);
                                ctx.emit(events.update, response.data);
                            }
                        }
                    }
                    catch (err) {
                        console.log(err);
                        showMessage("Error adding funds to promotion.", "danger", true, true);
                        ctx.emit(events.cancel);
                    }
                    finally {
                        data.isLoading.value = false;
                    }
                }
            }
        },
        creditCardChange: function (ev: Event) {
            if (data.selectedCreditCard.value === undefined) {
                data.displayAddCreditCard.value = true;
            } else {
                data.displayAddCreditCard.value = false;
            }
        },
        addCreditCardCancel: async function () {
            if (data.creditCards.value.length > 0) {
                data.displayAddCreditCard.value = false;
                let selected = data.creditCards.value.find(c => c.isPrimary === true);
                if (selected) {
                    data.selectedCreditCard.value = selected;
                }
                else {
                    data.selectedCreditCard.value = data.creditCards.value[0];
                }
            } else {
                ctx.emit(events.cancel);
            }
        },
        addCreditCardSave: async function (card: CreditCardModel) {
            if (card) {
                data.creditCards.value.splice(0, 0, card);
                data.selectedCreditCard.value = card;
                data.displayAddCreditCard.value = false;
            }
        }
    }
}

const hasErrors = function (data: IData) {
    return Object.keys(data.errors.value).length > 0;
}

const validateAddFunds = function (data: IData) {
    data.errors.value = {};

    if (data.applyCreditToPromotion.value) {
        //set value
        data.amount.value = data.accountCredit.value;
    } else {
        if (data.amount.value <= 0) {
            data.errors.value['amount'] = 'Please enter a positive amount to add to your promotion.';
        }
        if (!data.selectedCreditCard.value) {
            data.errors.value['selectedCreditCard'] = 'Please select or add a credit card.';
        }
        if (!isCsrOrAdmin() && (!data.securityCode.value || data.securityCode.value.length < 2)) {
            data.errors.value['securityCode'] = 'Please enter a valid security code for your credit card.';
        }
    }
}

const assignCreditCard = function (data: IData) {
    data.selectedCreditCard.value = data.creditCards.value.find(c => c.isPrimary === true);
}

const loadCreditCards = async function (data: IData, props: any, ctx: any) {
    try {
        let creditCardService = await context.getService<ICreditCardService>('ICreditCardService');
        let query = {
            accountId: props.promotion.accountId,
            statuses: [CreditCardStatus.Active]
        } as CreditCardQueryOptions;

        var response = await creditCardService.searchCards(query);

        for (var i = 0; i < response.length; i++) {
            data.creditCards.value.push(response[i]);
        }

        assignCreditCard(data);

    } catch (err) {
        console.log('Error loading credit cards.', err);
        showMessage("Error loading credit cards", "danger", true, true);
        ctx.emit(events.cancel);
    }
}

const loadBillingPreferences = async function (data: IData, props: any) {
    try {
        let billingService = await context.getService<IBillingService>('IBillingService');
        var response = await billingService.getPreferences(props.promotion.accountId);

        data.accountCredit.value = response?.credit ?? 0;

        if (data.accountCredit.value > 0) {
            data.amount.value = data.accountCredit.value;
            data.applyCreditToPromotion.value = true;
        }
    } catch (ex) {
        data.accountCredit.value = 0;
    }
}



const events = {
    update: 'update',
    cancel: 'cancel'
}

export default defineComponent({
    props: {
        promotion: {
            type: Object as () => PromotionModel,
            required: true
        },
        taxname: {
            type: String,
            required: true
        },
        taxrate: {
            type: Number,
            required: true
        }
    },
    components: {
        'error': validationError,
        'add-credit-card': addCreditCard
    },
    emits: [events.cancel, events.update],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(props, data, ctx);
        let computed = getComputed(data);

        onMounted(async () => {
            await loadBillingPreferences(data, props);
            await loadCreditCards(data, props, ctx);
        });

        return {
            ...data,
            ...methods,
            ...computed,
            'isCsrOrAdmin': isCsrOrAdmin,
            'PaymentMethod': PaymentMethod
        };
    }
});